import { Transition } from '@headlessui/react';
import useResizeObserver from '@react-hook/resize-observer';
import { clsx } from 'clsx';
import { debounce } from 'lodash';
import { type ElementRef, useRef, useState } from 'react';
import { Link, type LinkProps } from 'turnip/Link/Link';
import { styledLinkClasses } from 'turnip/StyledLink/styledLinkClasses';
import type { Maybe } from 'types/Utilities';

type TableProps = {
  caption: string;
  children: React.ReactNode;
  /** Whether the table has a border at the top; does not apply if `roundedBorder` is true. */
  topBorder?: boolean;
  roundedBorder?: boolean;
  /** Tailwind min-width class */
  minWidth?: string;
};

/**
 * These components are raw UI. They should encapsulate the design of our table
 * elements, regardless of how the data is generated and organized, how
 * features like sorting or pagination are implemented, etc..
 *
 * In other words, if we were to switch table libraries, these
 * components should not need to change.
 */

function Table({
  caption,
  children,
  topBorder = true,
  roundedBorder,
  minWidth,
}: TableProps) {
  const { containerRef, canScrollLeft, canScrollRight, onScroll } =
    useElementCanScroll();
  const canScroll = canScrollLeft || canScrollRight;

  return (
    <div>
      {canScroll && (
        <p className="text-sm">
          <strong>Tip:</strong> scroll the table to see all the content.
        </p>
      )}

      <div
        className={clsx(
          'grid overflow-hidden',
          canScroll && 'mt-3',
          roundedBorder && 'rounded-lg border border-gray-300'
        )}
      >
        <div
          className="col-start-1 row-start-1 overflow-auto"
          ref={containerRef}
          onScroll={onScroll}
        >
          <table
            className={clsx(
              'h-px w-full border-separate border-spacing-0',
              topBorder && !roundedBorder && 'border-t border-gray-300',
              minWidth
            )}
          >
            <caption className="sr-only">{caption}</caption>

            {children}
          </table>
        </div>

        <div className="pointer-events-none relative col-start-1 row-start-1">
          {/* NOTE: this does not currently work with pinned columns */}
          <ScrollShadow show={canScrollLeft} side="left" />
          <ScrollShadow show={canScrollRight} side="right" />
        </div>
      </div>
    </div>
  );
}

function useElementCanScroll() {
  const containerRef = useRef<ElementRef<'div'>>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const onScroll = debounce(
    () => {
      if (!containerRef.current) {
        return;
      }
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;

      setCanScrollLeft(scrollLeft > 12);
      setCanScrollRight(scrollWidth - clientWidth - scrollLeft > 12);
    },
    50,
    { leading: true, trailing: true }
  );

  useResizeObserver(containerRef, onScroll);

  return {
    containerRef,
    canScrollLeft,
    canScrollRight,
    onScroll,
  };
}

/** Shadow shown over the left or right edge of the table when it's scrollable */
function ScrollShadow({
  show,
  side,
}: {
  show: boolean;
  side: 'left' | 'right';
}) {
  return (
    <Transition
      show={show}
      as="div"
      className={clsx(
        'pointer-events-none absolute inset-y-0 w-8 translate-x-0 shadow-[0_0_25px_-5px_rgba(0,0,0,0.1),_0_0px_10px_-6px_rgba(0,0,0,0.1)] shadow-gray-600 transition-transform duration-300 ease-out',
        side === 'left'
          ? '-left-8 data-[closed]:-translate-x-8'
          : '-right-8 data-[closed]:translate-x-8'
      )}
    ></Transition>
  );
}

/** Table header section (`<thead>`) */
function Head({ children }: { children: React.ReactNode }) {
  return (
    <thead className="text-sm/4 font-semibold text-gray-600">{children}</thead>
  );
}

/** Table header row (`<tr>`) */
function HeadRow({ children }: { children: React.ReactNode }) {
  return <tr className="bg-white">{children}</tr>;
}

/** Table body section (`<tbody>`) */
function Body({ children }: { children: React.ReactNode }) {
  return <tbody>{children}</tbody>;
}

/** Table body row (`<tr>`) */
function BodyRow({
  children,
}: {
  children: ((active: boolean) => React.ReactNode) | React.ReactNode;
}) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const active = hovered || focused;

  return (
    <tr
      className={clsx(
        'text-sm text-gray-900 odd:bg-white even:bg-indigo-50 focus:outline-none',
        // Row styles for when a CellLink within the row is hovered
        'odd:has-[.cell-link:hover]:bg-gray-100 even:has-[.cell-link:hover]:bg-[#eaf0f6]'
      )}
      tabIndex={0}
      onMouseOver={() => setHovered(true)}
      onMouseOut={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      {typeof children === 'function' ? children(active) : children}
    </tr>
  );
}

type CellType = 'head' | 'body';

type BaseCellProps = {
  align?: 'left' | 'right' | 'center';
  /**
   * Pin the cell to the left side of the table, offset by the specified
   * amount. The first pinned column should have a `pin` value of `0px` (the
   * default if `pin` is `true`).
   *
   * Subsequent columns should be offset by the width of the previous column if
   * you wish to keep both columns visible. Setting both to `0px` will result
   * in the first pinned column being covered by the second one when the user
   * scrolls the table.
   *
   * The easiest way to correctly pin a second column is to set `width` on the
   * first column and then set `pin` on the second column to the same value.
   */
  pin?: `${number}px` | boolean;
  lastPinned?: boolean;
  noWrap?: boolean;
  colSpan?: number;
  children: React.ReactNode;
};

type HeadCellProps = BaseCellProps & {
  id?: string;
  width?: `${number}px`;
  sortDirection?: Maybe<'asc' | 'desc' | false>;
};
type BodyCellProps = BaseCellProps & {
  bold?: boolean;
  tabularNumbers?: boolean;
  link?: CellLinkProps;
};

/** Internal: table cell (`<td>` or `<th>`) */
function Cell({
  id,
  type,
  align,
  pin,
  lastPinned,
  noWrap,
  bold,
  width,
  sortDirection,
  tabularNumbers,
  link,
  colSpan,
  children,
}: HeadCellProps & BodyCellProps & { type: CellType }) {
  const pinOffset = pin === true ? '0px' : pin;

  let textAlignment = 'text-left';
  if (align === 'right') {
    textAlignment = 'text-right';
  }
  if (align === 'center') {
    textAlignment = 'text-center';
  }

  const props = {
    className: clsx(
      'align-top',
      type === 'head'
        ? 'p-3 text-sm/4 font-semibold'
        : 'border-t border-t-gray-200 p-0 text-sm [--cell-px:0.75rem] [--cell-py:0.5rem]',
      textAlignment,
      pin && 'sticky z-10 bg-inherit',
      lastPinned && 'border-r border-gray-300',
      noWrap && 'whitespace-nowrap',
      type !== 'head' && bold && 'font-bold',
      type === 'body' && tabularNumbers && 'tabular-nums'
    ),
    style: {
      left: pinOffset || undefined,
      width: type === 'head' ? width : undefined,
    },
    colSpan,
  };

  if (type === 'head') {
    return (
      <th {...props} id={id} aria-sort={getAriaSort(sortDirection)}>
        {children}
      </th>
    );
  }

  return (
    <td {...props}>
      {link ? (
        <CellLink {...link}>{children}</CellLink>
      ) : (
        <div className="px-[--cell-px] py-[--cell-py]">{children}</div>
      )}
    </td>
  );
}

/** Table head cell (`<th>`) */
const HeadCell = (props: HeadCellProps) => <Cell {...props} type="head" />;
/** Table cell (`<td>`) */
const BodyCell = (props: BodyCellProps) => <Cell {...props} type="body" />;

function getAriaSort(sortDirection: HeadCellProps['sortDirection']) {
  switch (sortDirection) {
    case 'asc': {
      return 'ascending';
    }

    case 'desc': {
      return 'descending';
    }

    default: {
      return 'none';
    }
  }
}

type CellLinkProps = LinkProps & {
  /** Set to `true` for the primary link in the row, e.g., for the link in the main cell */
  isPrimary?: boolean;
};

/** Link that spans the entire cell */
const CellLink = ({ isPrimary, className, ...props }: CellLinkProps) => (
  <Link
    {...props}
    tabIndex={isPrimary ? 0 : -1}
    className={clsx(
      'cell-link block h-full',
      // Match padding of cells without links
      'px-[--cell-px] py-[--cell-py]',
      // Style the primary link
      isPrimary && styledLinkClasses({ variant: 'strong', display: 'block' }),
      // Improve focus styles on the primary link
      isPrimary && 'relative z-10'
    )}
  />
);

Table.Head = Head;
Table.HeadRow = HeadRow;
Table.HeadCell = HeadCell;
Table.Body = Body;
Table.BodyRow = BodyRow;
Table.BodyCell = BodyCell;
Table.CellLink = CellLink;

export { Table };
export type {
  BaseCellProps,
  BodyCellProps,
  CellType,
  HeadCellProps,
  TableProps,
};
